import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import ConstructionSearch from '../../components/ConstructionSearch';
import { invalidateProjectNodes } from '../../lib/constructionCache';
import { Loader2, Plus, Trash2, Pencil, Building2, ChevronLeft, Layers, Check, X, Archive, RotateCcw, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOCATION_KINDS = ['site', 'building', 'zone', 'floor', 'space'];
const NODE_TABS = [
  { key: 'locations', label: 'Locations' },
  { key: 'trades', label: 'Trades' },
  { key: 'wbs', label: 'WBS' },
  { key: 'activities', label: 'Activities' },
  { key: 'materials', label: 'Materials' },
  { key: 'reporting', label: 'Reporting' },
];

const GROUP_OPTIONS = [
  { field: 'buildingId', label: 'Building' },
  { field: 'floorId', label: 'Floor' },
  { field: 'zoneId', label: 'Zone' },
  { field: 'tradeId', label: 'Trade' },
  { field: 'activityId', label: 'Activity' },
];

const kindBadge = {
  site: 'bg-slate-100 text-slate-700',
  building: 'bg-blue-100 text-blue-700',
  floor: 'bg-teal-100 text-teal-700',
  zone: 'bg-amber-100 text-amber-700',
  space: 'bg-purple-100 text-purple-700',
};

const ConstructionStructure = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState('locations');

  // Locations
  const [tree, setTree] = useState([]);
  const [flatLocations, setFlatLocations] = useState([]);
  const [locForm, setLocForm] = useState({ name: '', kind: 'building', parentId: '', code: '' });

  // Generic nodes (trades/wbs/activities/materials)
  const [nodes, setNodes] = useState([]);
  const [nodeForm, setNodeForm] = useState({ name: '', code: '' });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // {id, name, code, ...}
  const [showArchived, setShowArchived] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [groupBy, setGroupBy] = useState('buildingId');
  const [breakdown, setBreakdown] = useState(null);

  const loadProject = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}`);
      setProject(r.data);
    } catch (e) { /* */ }
  }, [projectId]);

  const loadLocations = useCallback(async () => {
    const inc = showArchived ? '?includeInactive=true' : '';
    const [treeRes, flatRes] = await Promise.all([
      axios.get(`${API}/projects/${projectId}/construction-tree${inc}`),
      axios.get(`${API}/projects/${projectId}/construction/locations${inc}`),
    ]);
    setTree(treeRes.data || []);
    setFlatLocations(flatRes.data || []);
  }, [projectId, showArchived]);

  const loadNodes = useCallback(async (nodeType) => {
    const inc = showArchived ? '?includeInactive=true' : '';
    const r = await axios.get(`${API}/projects/${projectId}/construction/${nodeType}${inc}`);
    setNodes(r.data || []);
  }, [projectId, showArchived]);

  const loadReporting = useCallback(async () => {
    const [cov, bd] = await Promise.all([
      axios.get(`${API}/projects/${projectId}/construction-coverage`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/projects/${projectId}/construction-breakdown`, { params: { groupBy } }).then((r) => r.data).catch(() => null),
    ]);
    setCoverage(cov);
    setBreakdown(bd);
  }, [projectId, groupBy]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'locations') await loadLocations();
      else if (tab === 'reporting') await loadReporting();
      else await loadNodes(tab);
    } catch (e) {
      toast.error('Failed to load construction data');
    } finally { setLoading(false); }
  }, [tab, loadLocations, loadNodes, loadReporting]);

  useEffect(() => { loadProject(); }, [loadProject]);
  useEffect(() => { setEditing(null); refresh(); }, [refresh]);

  // ---- Location actions ----
  const addLocation = async () => {
    if (!locForm.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/construction/locations`, {
        name: locForm.name.trim(),
        kind: locForm.kind,
        parentId: locForm.parentId || null,
        code: locForm.code.trim() || null,
      });
      toast.success(`${locForm.kind} added`);
      setLocForm({ name: '', kind: locForm.kind, parentId: locForm.parentId, code: '' });
      invalidateProjectNodes(projectId);
      await loadLocations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); } finally { setSaving(false); }
  };

  const saveLocationEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/projects/${projectId}/construction/locations/${editing.id}`, {
        name: editing.name, code: editing.code || null, kind: editing.kind, parentId: editing.parentId || null,
      });
      toast.success('Updated');
      setEditing(null);
      await loadLocations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); } finally { setSaving(false); }
  };

  // ---- Generic node actions ----
  const addNode = async () => {
    if (!nodeForm.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/construction/${tab}`, {
        name: nodeForm.name.trim(), code: nodeForm.code.trim() || null,
      });
      toast.success('Added');
      setNodeForm({ name: '', code: '' });
      invalidateProjectNodes(projectId);
      await loadNodes(tab);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); } finally { setSaving(false); }
  };

  const saveNodeEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/projects/${projectId}/construction/${tab}/${editing.id}`, {
        name: editing.name, code: editing.code || null,
      });
      toast.success('Updated');
      setEditing(null);
      await loadNodes(tab);
    } catch (e) { toast.error('Update failed'); } finally { setSaving(false); }
  };

  const archiveNode = async (nodeType, id, active) => {
    try {
      await axios.put(`${API}/projects/${projectId}/construction/${nodeType}/${id}/active`, { active });
      invalidateProjectNodes(projectId);
      toast.success(active ? 'Reactivated' : 'Archived');
      if (nodeType === 'locations') await loadLocations(); else await loadNodes(nodeType);
    } catch (e) { toast.error('Action failed'); }
  };

  const deleteWithSafety = async (nodeType, id, onDone) => {
    if (!window.confirm('Delete this item permanently?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/construction/${nodeType}/${id}`);
      invalidateProjectNodes(projectId);
      toast.success('Deleted');
      await onDone();
    } catch (e) {
      if (e.response?.status === 409) {
        const refs = e.response.data?.detail?.references || {};
        const summary = Object.entries(refs).map(([k, v]) => `${v} ${k}`).join(', ');
        if (window.confirm(`Cannot delete — referenced by ${summary}. Archive it instead so evidence stays linked?`)) {
          await archiveNode(nodeType, id, false);
        }
      } else {
        toast.error(e.response?.data?.detail || 'Delete failed');
      }
    }
  };

  const deleteLocation = (id) => deleteWithSafety('locations', id, loadLocations);
  const deleteNode = (id) => deleteWithSafety(tab, id, () => loadNodes(tab));

  const renderTreeNode = (node, depth = 0) => (
    <div key={node.id} data-testid="location-tree-node">
      <div className={`flex items-center gap-2 py-2 border-b border-slate-50 ${node.active === false ? 'opacity-50' : ''}`} style={{ paddingLeft: `${depth * 20}px` }}>
        <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${kindBadge[node.kind] || 'bg-slate-100 text-slate-600'}`}>{node.kind}</span>
        {node.active === false && <span className="text-[10px] uppercase text-amber-600 font-semibold">archived</span>}
        {editing && editing.id === node.id ? (
          <>
            <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-sm" data-testid="location-edit-name" />
            <button onClick={saveLocationEdit} disabled={saving} className="text-emerald-600 hover:text-emerald-700" data-testid="location-edit-save"><Check className="w-4 h-4" /></button>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </>
        ) : (
          <>
            <span className="text-sm font-medium text-slate-800">{node.name}</span>
            {node.code && <span className="text-xs text-slate-400">({node.code})</span>}
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => setEditing({ id: node.id, name: node.name, code: node.code || '', kind: node.kind, parentId: node.parentId || '' })} className="text-slate-400 hover:text-blue-600" data-testid="location-edit-btn"><Pencil className="w-3.5 h-3.5" /></button>
              {node.active === false ? (
                <button onClick={() => archiveNode('locations', node.id, true)} className="text-slate-400 hover:text-emerald-600" title="Reactivate" data-testid="location-reactivate-btn"><RotateCcw className="w-3.5 h-3.5" /></button>
              ) : (
                <button onClick={() => archiveNode('locations', node.id, false)} className="text-slate-400 hover:text-amber-600" title="Archive" data-testid="location-archive-btn"><Archive className="w-3.5 h-3.5" /></button>
              )}
              <button onClick={() => deleteLocation(node.id)} className="text-slate-400 hover:text-red-500" data-testid="location-delete-btn"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </>
        )}
      </div>
      {(node.children || []).map((c) => renderTreeNode(c, depth + 1))}
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6">
        <button onClick={() => navigate('/app/projects')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="back-to-projects">
          <ChevronLeft className="w-4 h-4" /> Projects
        </button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><Building2 className="w-7 h-7 text-blue-600" /> Construction Structure</h1>
          <p className="text-slate-600 mt-1">{project?.name || 'Project'} — configure the physical hierarchy and classifications captures link to.</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <ConstructionSearch projectId={projectId} onSelect={(item, group) => toast.info(`${group}: ${item.name || item.fileName}`)} />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="show-archived-toggle">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="rounded" />
            Show archived
          </label>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-slate-200" data-testid="construction-tabs">
          {NODE_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'locations' ? (
          <>
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm grid grid-cols-1 sm:grid-cols-5 gap-3 items-end" data-testid="location-form">
              <div>
                <label className="text-xs text-slate-500">Name</label>
                <input value={locForm.name} onChange={(e) => setLocForm({ ...locForm, name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="location-name-input" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Kind</label>
                <select value={locForm.kind} onChange={(e) => setLocForm({ ...locForm, kind: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="location-kind-select">
                  {LOCATION_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500">Parent (optional)</label>
                <select value={locForm.parentId} onChange={(e) => setLocForm({ ...locForm, parentId: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="location-parent-select">
                  <option value="">— none (root) —</option>
                  {flatLocations.map((l) => <option key={l.id} value={l.id}>{l.kind}: {l.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500">Code (optional)</label>
                <input value={locForm.code} onChange={(e) => setLocForm({ ...locForm, code: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="location-code-input" />
              </div>
              <button onClick={addLocation} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="location-add-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
            ) : tree.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400 flex flex-col items-center gap-2" data-testid="location-empty">
                <Layers className="w-8 h-8 text-slate-300" />
                No locations yet. Add a Site or Building above to start the hierarchy.
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="location-tree">
                {tree.map((n) => renderTreeNode(n))}
              </div>
            )}
          </>
        ) : tab === 'reporting' ? (
          <div className="space-y-6" data-testid="reporting-panel">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="coverage-cards">
                  {[
                    ['Locations', coverage?.totalLocations],
                    ['With evidence', coverage?.locationsWithEvidence],
                    ['No evidence', coverage?.locationsWithoutEvidence],
                    ['Captures', coverage?.totalCaptures],
                    ['Analyses', coverage?.totalAnalyses],
                    ['Issues', coverage?.totalIssues],
                  ].map(([label, val]) => (
                    <div key={label} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                      <p className="text-2xl font-bold text-slate-900">{val ?? 0}</p>
                      <p className="text-xs text-slate-500 mt-1">{label}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 -mt-3">Factual counts only — no progress percentages are inferred.</p>

                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600" /> Evidence by Construction Context</h3>
                    <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="breakdown-groupby">
                      {GROUP_OPTIONS.map((g) => <option key={g.field} value={g.field}>Group by {g.label}</option>)}
                    </select>
                  </div>
                  {(!breakdown || breakdown.rows.length === 0) ? (
                    <div className="text-center py-8 text-slate-400" data-testid="breakdown-empty">No tagged evidence for this grouping yet.</div>
                  ) : (
                    <table className="w-full text-sm" data-testid="breakdown-table">
                      <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="px-3 py-2">{GROUP_OPTIONS.find((g) => g.field === groupBy)?.label}</th><th className="px-3 py-2 text-right">Captures</th><th className="px-3 py-2 text-right">Analyses</th><th className="px-3 py-2 text-right">Measurements</th><th className="px-3 py-2 text-right">Issues</th><th className="px-3 py-2 text-right">Open</th></tr></thead>
                      <tbody>
                        {breakdown.rows.map((r) => (
                          <tr key={r.id} className="border-b border-slate-50" data-testid="breakdown-row">
                            <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                            <td className="px-3 py-2 text-right">{r.captures}</td>
                            <td className="px-3 py-2 text-right">{r.analyses}</td>
                            <td className="px-3 py-2 text-right">{r.measurements}</td>
                            <td className="px-3 py-2 text-right">{r.issues}</td>
                            <td className="px-3 py-2 text-right">{r.openIssues}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-3 items-end" data-testid="node-form">
              <div>
                <label className="text-xs text-slate-500">Name</label>
                <input value={nodeForm.name} onChange={(e) => setNodeForm({ ...nodeForm, name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="node-name-input" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Code (optional)</label>
                <input value={nodeForm.code} onChange={(e) => setNodeForm({ ...nodeForm, code: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="node-code-input" />
              </div>
              <button onClick={addNode} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="node-add-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
            ) : nodes.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400" data-testid="node-empty">No {tab} yet. Add one above.</div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="node-list">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="px-5 py-2">Name</th><th className="px-3 py-2">Code</th><th className="px-3 py-2 w-24"></th></tr></thead>
                  <tbody>
                    {nodes.map((n) => (
                      <tr key={n.id} className="border-b border-slate-50" data-testid="node-row">
                        {editing && editing.id === n.id ? (
                          <>
                            <td className="px-5 py-2"><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-sm w-full" data-testid="node-edit-name" /></td>
                            <td className="px-3 py-2"><input value={editing.code || ''} onChange={(e) => setEditing({ ...editing, code: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-sm w-full" /></td>
                            <td className="px-3 py-2 text-right flex items-center justify-end gap-2">
                              <button onClick={saveNodeEdit} disabled={saving} className="text-emerald-600 hover:text-emerald-700" data-testid="node-edit-save"><Check className="w-4 h-4" /></button>
                              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-5 py-3 font-medium text-slate-800">{n.name} {n.active === false && <span className="text-[10px] uppercase text-amber-600 font-semibold ml-1">archived</span>}</td>
                            <td className="px-3 py-3 text-slate-500">{n.code || '—'}</td>
                            <td className="px-3 py-3 text-right">
                              <div className="flex items-center justify-end gap-3">
                                <button onClick={() => setEditing({ id: n.id, name: n.name, code: n.code || '' })} className="text-slate-400 hover:text-blue-600" data-testid="node-edit-btn"><Pencil className="w-4 h-4" /></button>
                                {n.active === false ? (
                                  <button onClick={() => archiveNode(tab, n.id, true)} className="text-slate-400 hover:text-emerald-600" title="Reactivate" data-testid="node-reactivate-btn"><RotateCcw className="w-4 h-4" /></button>
                                ) : (
                                  <button onClick={() => archiveNode(tab, n.id, false)} className="text-slate-400 hover:text-amber-600" title="Archive" data-testid="node-archive-btn"><Archive className="w-4 h-4" /></button>
                                )}
                                <button onClick={() => deleteNode(n.id)} className="text-slate-400 hover:text-red-500" data-testid="node-delete-btn"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default ConstructionStructure;
